import { useRef, useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface Node {
  position: THREE.Vector3;
  category: 'ai' | 'security' | 'cloud' | 'systems';
  label: string;
}

const COLORS = {
  ai: '#00d9ff',      // quantum-blue
  security: '#39ff14', // quantum-green
  cloud: '#a855f7',    // quantum-purple
  systems: '#ff6b35',  // neural-orange
};

const NeuralNetwork = () => {
  const groupRef = useRef<THREE.Group>(null);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);

  // Define nodes (skills/technologies)
  const nodes: Node[] = useMemo(() => [
    // AI/ML Layer
    { position: new THREE.Vector3(-4, 3, 0), category: 'ai', label: 'Machine Learning' },
    { position: new THREE.Vector3(-2, 3.5, -1), category: 'ai', label: 'Neural Networks' },
    { position: new THREE.Vector3(-3, 2, 1), category: 'ai', label: 'AI Pipelines' },

    // Security Layer
    { position: new THREE.Vector3(2, 3, 0), category: 'security', label: 'Cybersecurity' },
    { position: new THREE.Vector3(4, 3.5, -1), category: 'security', label: 'SIGINT' },
    { position: new THREE.Vector3(3, 2, 1), category: 'security', label: 'Pentesting' },

    // Cloud Layer
    { position: new THREE.Vector3(-2, 0, -1), category: 'cloud', label: 'AWS Braket' },
    { position: new THREE.Vector3(0, -0.5, 0), category: 'cloud', label: 'Azure' },
    { position: new THREE.Vector3(2, 0, -1), category: 'cloud', label: 'Cloud Security' },

    // Systems Layer
    { position: new THREE.Vector3(-3, -2.5, 0), category: 'systems', label: 'Python' },
    { position: new THREE.Vector3(-1, -3, 1), category: 'systems', label: 'C++' },
    { position: new THREE.Vector3(1, -3, 1), category: 'systems', label: 'Go' },
    { position: new THREE.Vector3(3, -2.5, 0), category: 'systems', label: 'HPC' },
  ], []);

  // Generate connections between related nodes
  const connections = useMemo(() => {
    const conns: [number, number][] = [];

    // Connect nodes within proximity or logical relationships
    nodes.forEach((node, i) => {
      nodes.forEach((otherNode, j) => {
        if (i < j) {
          const distance = node.position.distanceTo(otherNode.position);
          if (distance < 4 || Math.random() > 0.6) {
            conns.push([i, j]);
          }
        }
      });
    });

    return conns;
  }, [nodes]);

  // Create line geometry for connections
  const lineGeometry = useMemo(() => {
    const points: THREE.Vector3[] = [];

    connections.forEach(([i, j]) => {
      points.push(nodes[i].position.clone());
      points.push(nodes[j].position.clone());
    });

    return new THREE.BufferGeometry().setFromPoints(points);
  }, [connections, nodes]);

  // Animation
  useFrame((state) => {
    if (!groupRef.current) return;

    const time = state.clock.getElapsedTime();

    // Gentle rotation
    groupRef.current.rotation.y = Math.sin(time * 0.2) * 0.3;
    groupRef.current.rotation.x = Math.cos(time * 0.15) * 0.1;
  });

  return (
    <group ref={groupRef}>
      {/* Connections */}
      <lineSegments geometry={lineGeometry}>
        <lineBasicMaterial
          color="#00d9ff"
          transparent
          opacity={0.3}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>

      {/* Nodes */}
      {nodes.map((node, index) => (
        <mesh
          key={index}
          position={node.position}
          scale={hoveredNode === index ? 1.3 : 1}
          onPointerOver={() => setHoveredNode(index)}
          onPointerOut={() => setHoveredNode(null)}
        >
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial
            color={COLORS[node.category]}
            emissive={COLORS[node.category]}
            emissiveIntensity={hoveredNode === index ? 1 : 0.5}
            transparent
            opacity={0.9}
          />
        </mesh>
      ))}

      {/* Lighting */}
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#39ff14" />
    </group>
  );
};

export default NeuralNetwork;
